import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, Save, Trash2, X as XIcon, AlertTriangle, Loader2 } from 'lucide-react';
import { useLanguage } from '../../i18n/index.jsx';
import contactService from '../../services/contactService';
import { ContactFormFields, formFromContact, formToPayload } from './contactForm.jsx';
import { userErrorMessage } from '../../utils/userError';
import Modal from '../ui/Modal';

const notifyContactChanged = (id) => {
    try { window.dispatchEvent(new CustomEvent('omnis:contact-changed', { detail: { id } })); } catch (_) { /* ignore */ }
};

const formatRelative = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (_) { return iso; }
};

// A small centered confirm dialog for the danger-zone actions.
const ConfirmDialog = ({ testid, title, body, confirmLabel, confirmTestid, busy, onConfirm, onCancel }) => {
    const { t } = useLanguage();
    return (
        <Modal onClose={onCancel} zIndex="z-[90]" size="sm" cardClassName="p-4" cardTestId={testid}>
                <div className="flex items-start gap-2 mb-3">
                    <AlertTriangle size={18} className="text-pai-red flex-shrink-0 mt-0.5" />
                    <h3 className="text-sm font-semibold text-white">{title}</h3>
                </div>
                <p className="text-xs text-pai-secondary mb-4 whitespace-pre-wrap">{body}</p>
                <div className="flex justify-end gap-2">
                    <button
                        onClick={onCancel}
                        disabled={busy}
                        className="px-3 py-1.5 text-sm text-pai-secondary hover:bg-white/[0.07] rounded-chip"
                    >
                        {t('editCancelBtn')}
                    </button>
                    <button
                        data-testid={confirmTestid}
                        onClick={onConfirm}
                        disabled={busy}
                        className="pai-btn-danger disabled:opacity-50"
                    >
                        {busy && <Loader2 size={13} className="animate-spin" />}
                        {confirmLabel}
                    </button>
                </div>
            </Modal>
    );
};

// Relationship Management View — the full-screen-in-panel edit overlay (FE-4).
// Reuses ContactFormFields (no PAI-username linking field) and adds the notes
// timeline + danger zone (Uncontact / Block / Delete).
const ContactEditOverlay = ({ contact: initialContact, onClose, onSaved, onDeleted, onSevered }) => {
    const { t } = useLanguage();
    const [contact, setContact] = useState(initialContact);
    const [form, setForm] = useState(() => formFromContact(initialContact));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [noteDraft, setNoteDraft] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    // Danger-zone confirm dialogs + busy.
    const [confirm, setConfirm] = useState(null); // 'uncontact' | 'block' | 'delete' | null
    const [dangerBusy, setDangerBusy] = useState(false);
    // True once the USER has edited any form field. Guards the async on-open
    // hydration below from clobbering typed input: the getContact() refresh can
    // resolve AFTER the user started typing (fast typist / slow network — QA
    // caught it deterministically in contacts-hub.spec.js), and re-seeding the
    // form at that point silently reverted the edit so Save PATCHed the ORIGINAL
    // values. A ref (not state) — it must be readable inside the effect's async
    // continuation without re-running the effect.
    const formDirtyRef = useRef(false);

    // The setter handed to the form fields: any user-driven change marks the
    // form dirty BEFORE delegating to setForm.
    const setFormFromUser = useCallback((updater) => {
        formDirtyRef.current = true;
        setForm(updater);
    }, []);

    // Fetch the full contact (notes_log, latest fields) on open. `setContact`
    // stays unconditional (notes/tier/email freshness); the FORM is only
    // re-seeded while still pristine.
    useEffect(() => {
        let cancelled = false;
        formDirtyRef.current = false; // a different contact ⇒ a fresh, pristine form
        (async () => {
            try {
                const fresh = await contactService.getContact(initialContact.id);
                if (!cancelled) {
                    setContact(fresh);
                    if (!formDirtyRef.current) setForm(formFromContact(fresh));
                }
            } catch (_) { /* keep the passed-in contact */ }
        })();
        return () => { cancelled = true; };
    }, [initialContact.id]);

    const primaryEmail = contact.email || (contact.emails && contact.emails[0]) || '';
    const isNetwork = contact.tier === 'network';

    const handleSave = async () => {
        if (!form.name.trim()) { setError(t('contactNameRequired')); return; }
        setSaving(true);
        setError(null);
        try {
            const updated = await contactService.updateContact(contact.id, formToPayload(form));
            notifyContactChanged(contact.id);
            onSaved?.(updated);
            onClose?.();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setSaving(false);
        }
    };

    const handleAddNote = async () => {
        if (!noteDraft.trim()) return;
        setAddingNote(true);
        try {
            await contactService.addNote(contact.id, { content: noteDraft.trim(), source: 'manual' });
            const refreshed = await contactService.getContact(contact.id);
            setContact(refreshed);
            setNoteDraft('');
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setAddingNote(false);
        }
    };

    const handleDeleteNote = async (noteId) => {
        try {
            await contactService.deleteNote(contact.id, noteId);
            const refreshed = await contactService.getContact(contact.id);
            setContact(refreshed);
        } catch (err) {
            setError(userErrorMessage(err, t));
        }
    };

    const runUncontact = useCallback(async () => {
        setDangerBusy(true);
        setError(null);
        try {
            const res = await contactService.uncontact(contact.id);
            notifyContactChanged(contact.id);
            onSevered?.(contact, { severed: true, summary: res?.summary });
            onClose?.();
        } catch (err) {
            setError(userErrorMessage(err));
            setConfirm(null);
        } finally {
            setDangerBusy(false);
        }
    }, [contact, onSevered, onClose]);

    const runBlock = useCallback(async () => {
        if (!primaryEmail) { setConfirm(null); return; }
        setDangerBusy(true);
        setError(null);
        try {
            await contactService.block(primaryEmail);
            notifyContactChanged(contact.id);
            onSevered?.(contact, { blocked: true });
            onClose?.();
        } catch (err) {
            setError(userErrorMessage(err));
            setConfirm(null);
        } finally {
            setDangerBusy(false);
        }
    }, [contact, primaryEmail, onSevered, onClose]);

    const runDelete = useCallback(async () => {
        setDangerBusy(true);
        setError(null);
        try {
            await contactService.deleteContact(contact.id);
            notifyContactChanged(contact.id);
            onDeleted?.(contact);
            onClose?.();
        } catch (err) {
            setError(userErrorMessage(err));
            setConfirm(null);
        } finally {
            setDangerBusy(false);
        }
    }, [contact, onDeleted, onClose]);

    // Opaque base: the overlay covers the panel body, so it must not let the
    // tier list behind it show through the glass.
    return (
        <div data-testid="contact-edit-overlay" className="absolute inset-0 z-20 flex flex-col bg-pai-base">
            {/* Header row */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-pai-subtle flex-shrink-0">
                <button
                    onClick={onClose}
                    className="p-1.5 text-pai-meta hover:text-white rounded-ctl transition-colors"
                    title={t('editCancelBtn')}
                >
                    <ChevronLeft size={18} />
                </button>
                <h3 className="flex-1 min-w-0 text-sm font-semibold text-white truncate px-2">
                    {t('editContactTitle')}
                </h3>
                <button
                    data-testid="contact-edit-save"
                    onClick={handleSave}
                    disabled={saving}
                    className="pai-btn-primary h-8 px-3 text-xs disabled:opacity-50"
                >
                    <Save size={13} /> {saving ? t('editSaving') : t('editSaveBtn')}
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {error && (
                    <div className="bg-pai-red/[0.08] border border-pai-red/40 rounded-ctl p-2 text-xs text-pai-red">
                        {error}
                    </div>
                )}

                <div className="bg-white/[0.03] rounded-row border border-pai-subtle p-3 space-y-3">
                    <ContactFormFields form={form} setForm={setFormFromUser} />
                </div>

                {/* Notes timeline (existing ContactNote UI). */}
                <section className="bg-white/[0.03] rounded-row border border-pai-subtle p-3">
                    <div className="text-[11px] uppercase tracking-wide font-semibold text-pai-meta mb-2">
                        {t('contactTimeline')}
                    </div>
                    <div className="flex gap-2 mb-2">
                        <input
                            type="text"
                            value={noteDraft}
                            onChange={(e) => setNoteDraft(e.target.value)}
                            placeholder={t('contactAddNotePlaceholder')}
                            className="flex-1 px-2 py-1 text-xs pai-input"
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNote(); } }}
                        />
                        <button
                            onClick={handleAddNote}
                            disabled={addingNote || !noteDraft.trim()}
                            className="px-2.5 py-1 text-xs font-semibold text-pai-void bg-white hover:bg-white/[0.88] disabled:opacity-50 rounded-chip transition-colors"
                        >
                            {t('contactAddNoteBtn')}
                        </button>
                    </div>
                    {(contact.notes_log || []).length === 0 ? (
                        <p className="text-xs text-pai-faint italic">{t('contactNoNotes')}</p>
                    ) : (
                        <ul className="space-y-2 max-h-60 overflow-y-auto">
                            {(contact.notes_log || []).map((note) => (
                                <li key={note.id} className="group bg-white/[0.025] rounded-chip border border-pai-subtle p-2">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-[10px] uppercase tracking-wide text-pai-meta">
                                            {note.source || 'manual'} · {formatRelative(note.created_at)}
                                        </span>
                                        <button
                                            onClick={() => handleDeleteNote(note.id)}
                                            className="opacity-0 group-hover:opacity-100 pai-touch-reveal text-pai-faint hover:text-pai-red p-0.5 transition-opacity"
                                            title={t('auxDeleteNote', 'Delete note')}
                                        >
                                            <XIcon size={11} />
                                        </button>
                                    </div>
                                    <p className="text-xs text-pai-body mt-0.5 whitespace-pre-wrap">
                                        {note.content}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {/* Danger zone */}
                <section className="bg-pai-red/[0.05] border border-pai-red/[0.35] rounded-card p-3 space-y-2">
                    <div className="text-[11px] uppercase tracking-[0.11em] font-semibold text-pai-red">
                        {t('dangerZone')}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {isNetwork && (
                            <button
                                data-testid="contact-uncontact-btn"
                                onClick={() => setConfirm('uncontact')}
                                className="px-3 py-1.5 text-xs font-semibold text-pai-red border border-pai-red/40 bg-pai-red/[0.08] rounded-ctl hover:bg-pai-red/[0.16] transition-colors"
                            >
                                {t('uncontactBtn')}
                            </button>
                        )}
                        {!!primaryEmail && (
                            <button
                                data-testid="contact-block-btn"
                                onClick={() => setConfirm('block')}
                                className="px-3 py-1.5 text-xs font-semibold text-pai-red border border-pai-red/40 bg-pai-red/[0.08] rounded-ctl hover:bg-pai-red/[0.16] transition-colors"
                            >
                                {t('blockContactBtn')}
                            </button>
                        )}
                        <button
                            data-testid="contact-delete-btn"
                            onClick={() => setConfirm('delete')}
                            className="pai-btn-danger h-8 px-3 text-xs"
                        >
                            <Trash2 size={13} /> {t('deleteContactBtn')}
                        </button>
                    </div>
                </section>
            </div>

            {confirm === 'uncontact' && (
                <ConfirmDialog
                    testid="uncontact-confirm-modal"
                    title={t('uncontactConfirmTitle')}
                    body={t('uncontactDisclaimer')}
                    confirmLabel={t('uncontactConfirmBtn')}
                    confirmTestid="uncontact-confirm-btn"
                    busy={dangerBusy}
                    onConfirm={runUncontact}
                    onCancel={() => setConfirm(null)}
                />
            )}
            {confirm === 'block' && (
                <ConfirmDialog
                    testid="block-confirm-modal"
                    title={t('blockContactConfirmTitle')}
                    body={t('blockContactDisclaimer')}
                    confirmLabel={t('blockContactConfirmBtn')}
                    confirmTestid="block-confirm-btn"
                    busy={dangerBusy}
                    onConfirm={runBlock}
                    onCancel={() => setConfirm(null)}
                />
            )}
            {confirm === 'delete' && (
                <ConfirmDialog
                    testid="delete-confirm-modal"
                    title={t('deleteContactConfirmTitle', { name: contact.name || '' })}
                    body={t('cardCannotUndo')}
                    confirmLabel={t('deleteContactConfirmBtn')}
                    confirmTestid="delete-confirm-btn"
                    busy={dangerBusy}
                    onConfirm={runDelete}
                    onCancel={() => setConfirm(null)}
                />
            )}
        </div>
    );
};

export default ContactEditOverlay;
